import { inject, Injectable } from '@angular/core';
import { StatefulService } from '@app/service/stateful.service';
import { StatefulValue } from '@workflow/class/stateful-value.class';
import { VisibleFunctionsService } from '@workflow/ecp-workflow/dynamic-app/service/logic-functions/visible-functions.service';
import { LogicFnContext } from '@workflow/service/logic/logic.service';

const ALPHA_NUMERIC_FWSLASH_DOT_HYPHEN_HASH_SPACE_ONLY_RX = /^[A-Za-z0-9\s-.#/]*$/;
const ALPHA_NUMERIC_FWSLASH_DOT_HYPHEN_SPACE_APOSTROPHES_ONLY_RX = /^[A-Za-z0-9\s-./"'"]*$/;
/**
 * Methods on this service get registered as "logic functions" on LogicService
 */
@Injectable({ providedIn: 'root' })
export class OaoFieldValidationFunctionsService {
  private readonly statefulService = inject(StatefulService);

  constructor(public readonly visibleFunctionService: VisibleFunctionsService) {}
  /** Method to check any of bsa (Products & Services Offerings) checkbox checked */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public isRequireBSAProductserviceHasValue = (context: LogicFnContext): boolean => {
    const storagePathPrefix = 'api:oaoMemberInformation/businessInformation/bankSecrecyAct/productsAndServicesOfferingsList';
    const checkcashing = this.statefulService.wrap(`${storagePathPrefix}/checkcashing`).value as boolean;
    const moneytransmission = this.statefulService.wrap(`${storagePathPrefix}/moneytransmission`).value as boolean;
    const currencyorVirtualCurrency = this.statefulService.wrap(`${storagePathPrefix}/currencyorVirtualCurrency`).value as boolean;
    const saleorStoredCards = this.statefulService.wrap(`${storagePathPrefix}/saleorStoredCards`).value as boolean;
    const issuerorSeller = this.statefulService.wrap(`${storagePathPrefix}/issuerorSeller`).value as boolean;
    const productsAndServicesOfferings = this.statefulService.wrap(
      `api:oaoMemberInformation/businessInformation/bankSecrecyAct/productsAndServicesOfferings`
    ).value as boolean;
    return (
      productsAndServicesOfferings &&
      !(checkcashing || moneytransmission || currencyorVirtualCurrency || saleorStoredCards || issuerorSeller)
    );
  };
  /** Method to check any of bsa (Public Office in Forgein Governments) checkbox checked */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public isRequireBSAPOFGHasValue = (context: LogicFnContext): boolean => {
    const storagePathPrefix = 'api:oaoMemberInformation/businessInformation/bankSecrecyAct/publicOfficeForgeinGovernmentsList';
    let objResponsiblePartySelected = false;
    let objBeneficialOwnerSelected = false;
    let objAuthorizedSignersSelected = false;

    const defaultCount = 4;
    for (let i = 0; i < defaultCount; i++) {
      const isResponsiblePartySelected = this.statefulService.wrap(`${storagePathPrefix}/responsibleParties/${i}/isSelected`)
        .value as boolean;
      objResponsiblePartySelected = isResponsiblePartySelected ? true : objResponsiblePartySelected;
      if (objResponsiblePartySelected) {
        break;
      }
    }

    const BeneficialOwnerCount = this.statefulService.wrap(
      'api:oaoMemberInformation/businessInformation/additionalBeneficialOwnerCount'
    ).valueStringOnly;
    if (BeneficialOwnerCount && Number.parseInt(BeneficialOwnerCount) > 0) {
      for (let i = 0; i < Number.parseInt(BeneficialOwnerCount); i++) {
        const isBeneficialOwnerSelected = this.statefulService.wrap(`${storagePathPrefix}/beneficialOwner/${i}/isSelected`)
          .value as boolean;
        objBeneficialOwnerSelected = isBeneficialOwnerSelected ? true : objBeneficialOwnerSelected;
      }
    }

    const additionalAuthorizedCount = this.statefulService.wrap(
      'api:oaoMemberInformation/businessInformation/additionalAuthorizedCount'
    ).valueStringOnly;
    if (additionalAuthorizedCount && Number.parseInt(additionalAuthorizedCount) > 0) {
      for (let i = 0; i < Number.parseInt(additionalAuthorizedCount); i++) {
        const isAuthorizedSignersSelected = this.statefulService.wrap(`${storagePathPrefix}/authorizedSigners/${i}/isSelected`)
          .value as boolean;
        objAuthorizedSignersSelected = isAuthorizedSignersSelected ? true : objAuthorizedSignersSelected;
      }
    }

    const publicOfficeForgeinGovernments = this.statefulService.wrap(
      `api:oaoMemberInformation/businessInformation/bankSecrecyAct/publicOfficeForgeinGovernments`
    ).value as boolean;
    return publicOfficeForgeinGovernments && !(objResponsiblePartySelected || objBeneficialOwnerSelected || objAuthorizedSignersSelected);
  };
  /** Method to check any of bsa (Public Office in Forgein Governments) checkbox checked */
  public isRequireBSAACHHasValue = (context: LogicFnContext, storagePathPrefix: string): boolean => {
    const wiretransfers = this.statefulService.wrap(`${storagePathPrefix}/wiretransfers`).value as boolean;
    const cash = this.statefulService.wrap(`${storagePathPrefix}/cash`).value as boolean;
    const electronic = this.statefulService.wrap(`${storagePathPrefix}/electronic`).value as boolean;
    const checks = this.statefulService.wrap(`${storagePathPrefix}/checks`).value as boolean;
    const other = this.statefulService.wrap(`${storagePathPrefix}/other`).value as boolean;
    const achTransactions = this.statefulService.wrap(`api:oaoMemberInformation/businessInformation/bankSecrecyAct/achTransactions`)
      .value as boolean;
    return achTransactions && !(wiretransfers || cash || electronic || checks || other);
  };
  /** Check business membership has either Beneficial Owner or authorized signers */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public hasOwnersForBSA = (context: LogicFnContext): boolean => {
    let ResponsiblePartyAsBeneficialOwnersCount = 0;
    const ResponsiblePartyCount = this.statefulService.wrap(
      'api:oaoMemberInformation/businessInformation/responsiblePartyCount'
    ).valueStringOnly;
    if (ResponsiblePartyCount && Number.parseInt(ResponsiblePartyCount) > 0) {
      for (let i = 0; i < Number.parseInt(ResponsiblePartyCount); i++) {
        const percentage = this.statefulService.wrap(
          `api:oaoMemberInformation/businessInformation/responsibleParties/${i}/percentage`
        ).value;
        if (percentage) {
          ResponsiblePartyAsBeneficialOwnersCount += 1;
        }
      }
    }

    const BeneficialOwnerCount = this.statefulService.wrap(
      'api:oaoMemberInformation/businessInformation/additionalBeneficialOwnerCount'
    ).valueStringOnly;
    const additionalAuthorizedCount = this.statefulService.wrap(
      'api:oaoMemberInformation/businessInformation/additionalAuthorizedCount'
    ).valueStringOnly;
    const hasOwners =
      (BeneficialOwnerCount && Number.parseInt(BeneficialOwnerCount) > 0) ||
      (additionalAuthorizedCount && Number.parseInt(additionalAuthorizedCount) > 0) ||
      (ResponsiblePartyAsBeneficialOwnersCount && ResponsiblePartyAsBeneficialOwnersCount > 0);
    return hasOwners;
  };
  /** Set publicOfficeForgeinGovernments to false by default if no authorized siners available */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public setBSAPOFGValue = (context: LogicFnContext): void => {
    const hasOwners = this.hasOwnersForBSA(null);
    if (!hasOwners) {
      this.statefulService.wrap(`api:oaoMemberInformation/businessInformation/bankSecrecyAct/publicOfficeForgeinGovernments`).value = false;
    }
  };

  /** Check max 3 beneficial owners selected, if so then disable remaining options */
  public enablePublicOfficeForgeinGovernmentListField = (context: LogicFnContext, selectionPath: string): boolean => {
    // eslint-disable-next-line @typescript-eslint/ban-types
    const isCurrentItemSelected = this.statefulService.wrap(selectionPath).value as boolean;
    if (isCurrentItemSelected) {
      return true;
    } else {
      let count = 0;
      const defaultItertionCount = 4;
      const storagePathPrefix = 'api:oaoMemberInformation/businessInformation/bankSecrecyAct/publicOfficeForgeinGovernmentsList';
      for (let i = 0; i < defaultItertionCount; i++) {
        const responsiblePartySelected = this.statefulService.wrap(`${storagePathPrefix}/responsibleParties/${i}/isSelected`)
          .value as boolean;
        const beneficialOwnerSelected = this.statefulService.wrap(`${storagePathPrefix}/beneficialOwner/${i}/isSelected`).value as boolean;
        const autorizedSignerSelected = this.statefulService.wrap(`${storagePathPrefix}/authorizedSigners/${i}/isSelected`)
          .value as boolean;
        if (responsiblePartySelected) {
          count += 1;
        }
        if (beneficialOwnerSelected) {
          count += 1;
        }
        if (autorizedSignerSelected) {
          count += 1;
        }
      }
      return count <= 2;
    }
  };

  /**
   * Validation against given strings.
   * This includes alpha, numeric,slash,dot,hash, hyphen, spaces.
   * @param context field context
   */
  public alphaNumericwithSpecialCharacters = (context: LogicFnContext): boolean => {
    const value = context.field.readableValue;
    if (typeof value === 'string') {
      if (!value) {
        return !value;
      }
      const alphanumericIsmatched = !!value.match(ALPHA_NUMERIC_FWSLASH_DOT_HYPHEN_HASH_SPACE_ONLY_RX);
      return alphanumericIsmatched;
    }
  };

  /**
   * Validation against given strings.
   * This includes alpha, numeric,slash, dot, hyphen, spaces.
   * @param context field context
   */
  public alphaNumericwithSlashDotHyphenSpace = (context: LogicFnContext): boolean => {
    const value = context.field.readableValue;
    if (typeof value === 'string') {
      if (!value) {
        return !value;
      }
      const alphanumericIsmatched = !!value.match(ALPHA_NUMERIC_FWSLASH_DOT_HYPHEN_SPACE_APOSTROPHES_ONLY_RX);
      return alphanumericIsmatched;
    }
  };

  /**
   * Check Year & Month Period should be atleast 1 month.
   * @param _context
   * @param yearPath
   * @param monthPath
   * @returns
   */
  public minimumPeriodValidation = (_context: LogicFnContext, yearPath: string, monthPath: string): boolean => {
    const iterationID = _context.field.iterationId;
    const yearValue = this.statefulService.wrap(yearPath, iterationID, _context.field.pageIterationId).value;
    const monthValue = this.statefulService.wrap(monthPath, iterationID, _context.field.pageIterationId).value;

    if (yearValue && monthValue) {
      let totalmonth = 0;
      const month = this.statefulService.wrap(monthPath, iterationID, _context.field.pageIterationId).valueNumberOnly;
      const year = this.statefulService.wrap(yearPath, iterationID, _context.field.pageIterationId).valueNumberOnly;
      totalmonth += month;
      totalmonth += year * 12;
      return !(totalmonth < 1);
    }
    return true;
  };
  /**
   * Validate PO Box address
   */
  public validatePOBoxAddress = (context: LogicFnContext, addressField: string): boolean => {
    return !this.visibleFunctionService.checkPOBoxAddress(context, addressField);
  };

  /**
   * Validates whether the provided document path contains all required document information
   * for the specified context. It checks against a set of required uploader IDs to determine
   * document validity.
   * @param _context The context within which the document validation is performed, including
   *                 any specific field configurations.
   * @param docPath The path to the document to be validated.
   * @returns Returns `true` if the document meets all required criteria, otherwise `false`.
   */
  public validateRequiredDoc = (_context: LogicFnContext, docPath: string): boolean => {
    const rdcString = this.statefulService.wrap(docPath).valueString;
    if (!rdcString) return false;
    const rdcData = JSON.parse(rdcString);
    const uploadedByArray: number[] = rdcData.documents.map((doc) => doc.uploadedBy);
    let appliesTo = _context.field.config.appliesTo;
    const requiredUploader: number[] = this.getUploadedByArray();
    let isValid = true;
    // Setting the appliesTo value to 1 (Primary Applicant) when no default values are set in the Admin
    // Handled this scenario for showing up the relevant cards in US:#236502
    appliesTo = appliesTo === undefined || appliesTo === '' ? '1' : appliesTo;
    if (Number(appliesTo) === 0) {
      isValid = requiredUploader.every((value) => uploadedByArray.includes(value));
    } else if (requiredUploader.indexOf(Number(appliesTo))) {
      isValid = uploadedByArray.includes(Number(appliesTo) - 1);
    }
    return isValid;
  };

  // Get uploader code list
  public getUploadedByArray = (): number[] => {
    const uploadedByArray = [];
    const membershipTypeName = this.statefulService.wrap('api:oaoMemberInformation/membership/membershipTypeName').valueString;
    switch (membershipTypeName && membershipTypeName.toLowerCase()) {
      case 'personal':
        // Default Primary applicant uploaded by will be 0
        uploadedByArray.push(0);
        const hasCoApplicant = this.statefulService.wrap('api:oaoMemberInformation/hasCoApplicant').value as boolean;
        if (hasCoApplicant) {
          uploadedByArray.push(1);
        }
        const additionalCoApplicantOption = this.statefulService.wrap('api:oaoMemberInformation/hasAdditionalCoApplicant').value as boolean;
        if (additionalCoApplicantOption) {
          uploadedByArray.push(2);
        }
        break;
      case 'business':
        const responsiblePartyCount = this.statefulService.wrap(
          'api:oaoMemberInformation/businessInformation/responsiblePartyCount'
        ).valueStringOnly;
        if (Number.parseInt(responsiblePartyCount) > 0) {
          const responsibleParties = StatefulValue.arraySize('api:oaoMemberInformation/businessInformation/responsibleParties');
          for (let i = 0; i < responsibleParties; i++) {
            uploadedByArray.push(i);
          }
        }
        break;
      default:
        break;
    }
    return uploadedByArray;
  };

  /**
   * Logic to enable/disable TCPA Consent checkbox
   * @param context Field COntext
   * @param autofillOptionPath StorageId Path
   * @returns Boolean
   */
  public enableTCPAConsent = (context: LogicFnContext, parentPath: string, tcpaAcceptancePath: string): boolean => {
    const autoFillOptionPath = parentPath + '/autoFillOption';
    const autoFillStatusPath = parentPath + '/autoFillStatus';
    const autofillOption = this.statefulService.wrap(
      autoFillOptionPath,
      context.field.iterationId,
      context.field.pageIterationId
    ).valueStringOnly;
    const autoFillStatus = this.statefulService.wrap(autoFillStatusPath, context.field.iterationId, context.field.pageIterationId).value;
    const isTCPAAccepted = this.statefulService.wrap(tcpaAcceptancePath, context.field.iterationId, context.field.pageIterationId).value;
    const fieldValue = context.field?.data?.value;
    if (
      (autofillOption === 'MobileNumber' || autoFillStatus === true) &&
      isTCPAAccepted === true &&
      fieldValue &&
      Boolean(fieldValue) === true
    ) {
      return false;
    }
    return true;
  };
}
